import classnames from 'classnames';

import Icon, { icons } from '../Icon';

import ButtonBase from './Base';
import { BUTTON_SIZE, BUTTON_TYPE } from './button-constants';
import type { GetButtonProps } from './Base';
import type { LoaderButtonProps } from './LoaderButton';

const defaultMarginAmount = '0.35em'; // em is relative to the parent's font-size, so this should work for all sizes

/**
 * Button that accepts an `isLoading` prop which, if true, renders a button with a loader icon
 * side-by-side with the contents/children.
 *
 * If `isLoading` is false, it will just render the contents/children.
 *
 * Different from the `LoaderButton`, this Component will display the contents of the button
 * while loading is taking place.
 */
const InlineLoaderButton = ({
  type = BUTTON_TYPE.PRIMARY,
  size = BUTTON_SIZE.SMALL,
  icon,
  isDisabled,
  isFullWidth,
  isSubmitButton,
  children,
  onClick,
  testId,
  isLoading,
}: LoaderButtonProps) => (
  <ButtonBase
    type={type}
    size={size}
    isDisabled={isDisabled}
    isFullWidth={isFullWidth}
    isSubmitButton={isSubmitButton}
    onClick={onClick}
    testId={testId}
  >
    {(getButtonProps: GetButtonProps) => (
      // Note: disabling, because `getButtonProps` returns a button type.
      // eslint-disable-next-line react/button-has-type
      <button
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getButtonProps()}
      >
        <span className="fossa-button__contents">
          {icon && (
            <span className={classnames('fossa-button__icon', {
              'fossa-button__icon--solo': !children, // Remove margin if only child
            })}>
              <Icon icon={icon} />
            </span>
          )}
          <span className="fossa-button__text">
            {children}
          </span>
          {isLoading && (
            <span style={{ marginLeft: defaultMarginAmount }}>
              <Icon hasSpin icon={icons.circleO} />
            </span>
          )}
        </span>
      </button>
    )}
  </ButtonBase>
);

export default InlineLoaderButton;
